import {
  type EvidenceFileKind,
  resolveEvidenceFileKind,
} from "@repo/shared/document-evidence-link";
import {
  FileCode,
  FileSpreadsheet,
  FileText,
  type LucideIcon,
  MessageSquareText,
  UserPen,
} from "lucide-react";

/** The glyph that stands for a source, chosen by what the source is. */
export type EvidenceSourceIcon =
  | "file-text"
  | "file-spreadsheet"
  | "file-code"
  | "message"
  | "manual";

/**
 * The one glyph per source kind. A citation marker and the popover it opens draw the same
 * one, so what a reader clicks and what they then see are recognizably the same source.
 */
export const EVIDENCE_SOURCE_ICONS: Record<EvidenceSourceIcon, LucideIcon> = {
  "file-text": FileText,
  "file-spreadsheet": FileSpreadsheet,
  "file-code": FileCode,
  message: MessageSquareText,
  manual: UserPen,
};

export function evidenceFileIconKey(fileKind: EvidenceFileKind | undefined): EvidenceSourceIcon {
  switch (fileKind) {
    case "spreadsheet":
      return "file-spreadsheet";
    case "code":
      return "file-code";
    default:
      return "file-text";
  }
}

/**
 * The glyph for a cited document. A surface that subscribes to the presentation passes the
 * `fileKind` the server derived from the artifact; one that only holds a name reads what it
 * can out of that, and falls back to prose — which is what most cited documents are.
 */
export function evidenceDocumentIcon(source: {
  fileKind?: EvidenceFileKind;
  documentName?: string | null;
}): LucideIcon {
  const fileKind =
    source.fileKind ?? resolveEvidenceFileKind({ fileName: source.documentName ?? null });
  return EVIDENCE_SOURCE_ICONS[evidenceFileIconKey(fileKind)];
}
